/* eslint-disable no-param-reassign */
import produce from 'immer';
import {
  GET_SMART_FORMS,
  GET_SMART_FORMS_SUCCESS,
  GET_SMART_FORMS_ERROR,
  BUMP_SMART_FORMS_SUBMISSIONS,
  CLEAR_SMART_FORMS,
  GET_SMART_FORM,
  GET_SMART_FORM_SUCCESS,
  GET_SMART_FORM_ERROR,
  CLEAR_SMART_FORM,
  CREATE_SMART_FORM,
  CREATE_SMART_FORM_SUCCESS,
  CREATE_SMART_FORM_ERROR,
  UPDATE_SMART_FORM,
  UPDATE_SMART_FORM_SUCCESS,
  UPDATE_SMART_FORM_ERROR,
  DELETE_SMART_FORM,
  DELETE_SMART_FORM_SUCCESS,
  DELETE_SMART_FORM_ERROR,
  SET_SMART_FORMS_QUERY,
  SET_BACK_TO_SMART_FORMS_LIST,
} from 'src/actions/smartFormsActions';

const backToSmartFormsList = {
  smartForms: [],
  query: {},
  active: false,
  comeBack: false,
  backToId: null,
};

const defaultQuery = {
  search: '',
  page: 1,
  pageSize: 50,
  count: 0,
  sort: [],
  // The Status dropdown displays "Active" by default (SearchBox falls back to
  // 'Active' when status is unset). Seed the query with it too so the applied
  // filter matches the displayed default on first load — otherwise the list
  // shows both Active and Inactive forms while the dropdown reads "Active".
  status: 'Active',
  // "Show event & group join forms" toggle, default OFF. False hides the
  // instance-tied event/group join forms so users aren't buried under the
  // hundreds auto-generated per event/group after the V1->V2 conversion.
  includeLegacy: false,
  // CON-2036 review — the "Unseen (N)" filter (ACTIVITY_BADGES holders).
  newOnly: false,
};

const initialState = {
  smartForms: [],
  loading: false,
  hiddenLegacyCount: 0,
  query: { ...defaultQuery },
  smartFormDetails: {
    smartForm: {},
    smartFormWasDeleted: false,
    loading: false,
  },
  backToSmartFormsList,
};

const smartFormsReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case GET_SMART_FORMS: {
      return produce(state, (draft) => {
        draft.loading = true;
      });
    }

    case GET_SMART_FORMS_SUCCESS: {
      return produce(state, (draft) => {
        draft.smartForms = payload?.forms || [];
        draft.query.count = payload?.count || 0;
        draft.hiddenLegacyCount = payload?.hiddenLegacyCount || 0;
        draft.loading = false;
      });
    }

    case BUMP_SMART_FORMS_SUBMISSIONS: {
      if (!payload) return state;
      return produce(state, (draft) => {
        draft.smartForms.forEach((form) => {
          const delta = payload[form.id];
          if (delta > 0) {
            form.entryCounts = {
              ...(form.entryCounts || {}),
              submissions: ((form.entryCounts || {}).submissions || 0) + delta,
            };
          }
        });
      });
    }

    case GET_SMART_FORMS_ERROR: {
      return produce(state, (draft) => {
        draft.loading = false;
      });
    }

    case CLEAR_SMART_FORMS: {
      return produce(state, (draft) => {
        draft.smartForms = [];
      });
    }

    case GET_SMART_FORM:
    case CREATE_SMART_FORM:
    case UPDATE_SMART_FORM: {
      return produce(state, (draft) => {
        draft.smartFormDetails.loading = true;
      });
    }

    case GET_SMART_FORM_SUCCESS: {
      return produce(state, (draft) => {
        draft.smartFormDetails.smartForm = payload || {};
        draft.smartFormDetails.loading = false;
      });
    }

    case GET_SMART_FORM_ERROR:
    case CREATE_SMART_FORM_SUCCESS:
    case CREATE_SMART_FORM_ERROR:
    case UPDATE_SMART_FORM_ERROR: {
      return produce(state, (draft) => {
        draft.smartFormDetails.loading = false;
      });
    }

    case UPDATE_SMART_FORM_SUCCESS: {
      return produce(state, (draft) => {
        if (payload) {
          draft.smartFormDetails.smartForm = {
            ...state.smartFormDetails.smartForm,
            ...payload,
          };
        }
        draft.smartFormDetails.loading = false;
      });
    }

    case CLEAR_SMART_FORM: {
      return produce(state, (draft) => {
        draft.smartFormDetails.smartForm = {};
      });
    }

    case DELETE_SMART_FORM: {
      return produce(state, (draft) => {
        draft.loading = true;
        draft.smartFormDetails.loading = true;
      });
    }

    case DELETE_SMART_FORM_SUCCESS: {
      return produce(state, (draft) => {
        draft.smartFormDetails.loading = false;
        draft.loading = false;
        draft.smartFormDetails.smartFormWasDeleted = true;
      });
    }

    case DELETE_SMART_FORM_ERROR: {
      return produce(state, (draft) => {
        draft.smartFormDetails.loading = false;
        draft.loading = false;
      });
    }

    case SET_SMART_FORMS_QUERY: {
      return produce(state, (draft) => {
        draft.query = payload ? {
          ...state.query,
          ...payload,
        } : { ...defaultQuery };
      });
    }

    case SET_BACK_TO_SMART_FORMS_LIST: {
      return produce(state, (draft) => {
        draft.backToSmartFormsList = payload ? {
          ...state.backToSmartFormsList,
          ...payload,
        } : backToSmartFormsList;
      });
    }

    default: {
      return state;
    }
  }
};

export default smartFormsReducer;
